import { useCallback, useMemo, useState } from "react";

import { useAllDocumentsSelect, useAllFilesSelect, useAllFormsSelect, useFoldersSelect } from "store";
import { ISaveDealAsTemplateData } from "store/types/templates";

interface SelectItem {
  id: number;
  parentId: number | null;
}

interface SelectionState {
  folders: SelectItem[];
  files: SelectItem[];
  forms: SelectItem[];
  documents: SelectItem[];
}

type SelectionKey = keyof SelectionState;

const defaultState: SelectionState = { folders: [], files: [], forms: [], documents: [] };

export const useTemplateContentSelection = () => {
  const [state, setState] = useState<SelectionState>(defaultState);
  const folders = useFoldersSelect({ kind: "folder" });
  const files = useAllFilesSelect();
  const forms = useAllFormsSelect();
  const documents = useAllDocumentsSelect();

  const getAllDescendantFolderIds = useCallback(
    (parentId: number): number[] => {
      const children = folders.filter((f) => f.parent_id === parentId);
      const ids: number[] = [];
      children.forEach((child) => {
        ids.push(child.id);
        ids.push(...getAllDescendantFolderIds(child.id));
      });
      return ids;
    },
    [folders]
  );

  const getAncestorFolderIds = useCallback(
    (id: number): SelectItem[] => {
      const result: SelectItem[] = [];
      const folder = folders.find((f) => f.id === id);
      if (folder) {
        result.push({ id: folder.id, parentId: folder.parent_id || null });
        if (folder.parent_id) {
          result.push(...getAncestorFolderIds(folder.parent_id));
        }
      }
      return result;
    },
    [folders]
  );

  const isSelectedAll: boolean = useMemo(() => {
    const nonPrivateFolders = folders.filter((f) => !f.is_private);
    const nonPrivateFiles = files.filter((f) => !f.is_private);
    const nonPrivateForms = forms.filter((f) => !f.is_private);
    const nonPrivateDocuments = documents.filter((d) => !d.is_private);

    return (
      state.folders.length === nonPrivateFolders.length &&
      state.files.length === nonPrivateFiles.length &&
      state.forms.length === nonPrivateForms.length &&
      state.documents.length === nonPrivateDocuments.length
    );
  }, [state, folders, files, forms, documents]);

  const toggleFolder = useCallback(
    (id: number) => {
      const isCurrentlySelected = state.folders.some((f) => f.id === id);

      if (isCurrentlySelected) {
        const descendantIds = getAllDescendantFolderIds(id);
        const idsToRemove = new Set([id, ...descendantIds]);

        setState({
          folders: state.folders.filter((f) => !idsToRemove.has(f.id)),
          files: state.files.filter((f) => f.parentId === null || !idsToRemove.has(f.parentId)),
          forms: state.forms.filter((f) => f.parentId === null || !idsToRemove.has(f.parentId)),
          documents: state.documents.filter((d) => d.parentId === null || !idsToRemove.has(d.parentId))
        });
      } else {
        const descendantIds = getAllDescendantFolderIds(id);
        const allFolderIds = [id, ...descendantIds];

        const ancestors = getAncestorFolderIds(id);
        const newFolders = [...state.folders];

        ancestors.forEach((ancestor) => {
          if (!newFolders.some((f) => f.id === ancestor.id)) {
            newFolders.push(ancestor);
          }
        });

        allFolderIds.forEach((folderId) => {
          const folder = folders.find((f) => f.id === folderId);
          if (folder && !newFolders.some((f) => f.id === folderId)) {
            newFolders.push({ id: folderId, parentId: folder.parent_id || null });
          }
        });

        const newFiles = [...state.files];
        const newForms = [...state.forms];
        const newDocuments = [...state.documents];

        allFolderIds.forEach((folderId) => {
          files
            .filter((f) => f.section_id === folderId)
            .forEach((f) => {
              if (!newFiles.some((sf) => sf.id === f.id)) {
                newFiles.push({ id: f.id, parentId: folderId });
              }
            });

          forms
            .filter((f) => f.section_id === folderId)
            .forEach((f) => {
              if (!newForms.some((sf) => sf.id === f.id)) {
                newForms.push({ id: f.id, parentId: folderId });
              }
            });

          documents
            .filter((d) => d.section_id === folderId)
            .forEach((d) => {
              if (!newDocuments.some((sd) => sd.id === d.id)) {
                newDocuments.push({ id: d.id, parentId: folderId });
              }
            });
        });

        setState({ folders: newFolders, files: newFiles, forms: newForms, documents: newDocuments });
      }
    },
    [state, folders, files, forms, documents, getAllDescendantFolderIds, getAncestorFolderIds]
  );

  const toggleItem = useCallback(
    (key: Exclude<SelectionKey, "folders">, item: SelectItem) => {
      const isCurrentlySelected = state[key].some((el) => el.id === item.id);
      const newState = { ...state };

      if (isCurrentlySelected) {
        newState[key] = newState[key].filter((el) => el.id !== item.id);
      } else {
        newState[key] = [...newState[key], item];

        // Auto-select parent folders up the tree
        if (item.parentId) {
          const ancestors = getAncestorFolderIds(item.parentId);
          ancestors.forEach((ancestor) => {
            if (!newState.folders.some((f) => f.id === ancestor.id)) {
              newState.folders = [...newState.folders, ancestor];
            }
          });
        }

        // If selecting a file with form_id, also select the form
        if (key === "files") {
          const selectedFile = files.find((f) => f.id === item.id);
          if (selectedFile?.form_id) {
            const form = forms.find((f) => f.id === selectedFile.form_id);
            if (form && !newState.forms.some((el) => el.id === form.id)) {
              newState.forms = [...newState.forms, { id: form.id, parentId: form.section_id }];
              if (form.section_id && !newState.folders.some((f) => f.id === form.section_id)) {
                const formAncestors = getAncestorFolderIds(form.section_id);
                formAncestors.forEach((ancestor) => {
                  if (!newState.folders.some((f) => f.id === ancestor.id)) {
                    newState.folders = [...newState.folders, ancestor];
                  }
                });
              }
            }
          }
        }
      }

      setState(newState);
    },
    [state, files, forms, getAncestorFolderIds]
  );

  const toggleAll = useCallback(() => {
    if (isSelectedAll) {
      setState(defaultState);
      return;
    }

    const folderItems: SelectItem[] = folders
      .filter((f) => !f.is_private)
      .map((f) => ({ id: f.id, parentId: f.parent_id || null }));

    const fileItems: SelectItem[] = files
      .filter((f) => !f.is_private)
      .map((f) => ({ id: f.id, parentId: f.section_id }));

    const formItems: SelectItem[] = forms
      .filter((f) => !f.is_private)
      .map((f) => ({ id: f.id, parentId: f.section_id }));

    const documentItems: SelectItem[] = documents
      .filter((d) => !d.is_private)
      .map((d) => ({ id: d.id, parentId: d.section_id ?? null }));

    setState({ folders: folderItems, files: fileItems, forms: formItems, documents: documentItems });
  }, [folders, files, forms, documents, isSelectedAll]);

  const isSelected = useCallback(
    (key: SelectionKey, id: number): boolean => {
      return state[key].some((item) => item.id === id);
    },
    [state]
  );

  const buildPayload = useCallback((): ISaveDealAsTemplateData["sections"] => {
    return state.folders.reduce(
      (acc, folder) => ({
        ...acc,
        [folder.id]: {
          parent_id: folder.parentId,
          documents: state.documents.filter((d) => d.parentId === folder.id).map((d) => d.id),
          forms: state.forms.filter((f) => f.parentId === folder.id).map((f) => f.id),
          files: state.files.filter((f) => f.parentId === folder.id).map((f) => f.id)
        }
      }),
      {} as ISaveDealAsTemplateData["sections"]
    );
  }, [state]);

  return {
    state,
    isSelectedAll,
    toggleFolder,
    toggleItem,
    toggleAll,
    isSelected,
    buildPayload,
    folders,
    files,
    forms,
    documents
  };
};
